import type { BranchChangesets } from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetUpgradeBranchComparison } from '@festina/configuration-general-types/configuration/hooks/ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetUpgradeBranchComparison'
import {
  type BranchKey,
  useImportBranchChangesets,
  useInvalidateAllConciseBranches,
  useSquashBranch,
} from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo, useState } from 'react'

import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { useCreateBranch } from '../../../branchCreator/useCreateBranch'
import type { UpgradeResult, UpgradeStatus } from './types'
import type { UpgradeBranchInput } from './UpgradeBranchForm'

const useLazyFetchUpgradeComparison =
  ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetUpgradeBranchComparison.useLazyQuery

export const useUpgradeBranch = (branchKey: BranchKey) => {
  const translate = useTranslate()
  const conciseBranch = useConciseBranch({
    branchName: branchKey.name,
    server: branchKey.server,
    shouldFallbackToStandard: false,
  })
  const [status, setStatus] = useState<UpgradeStatus>()
  const [error, setError] = useState('')
  const [branchChangesetsPackage, setBranchChangesetsPackage] =
    useState<BranchChangesets>()
  const { mutateAsync: triggerUpgradeComparison } =
    useLazyFetchUpgradeComparison()
  const { mutateAsync: triggerCreateBranch } = useCreateBranch()
  const { mutateAsync: triggerImportChangesets } = useImportBranchChangesets()
  const invalidateBranches = useInvalidateAllConciseBranches()
  const invalidateBranchAfterHistoryRewrite =
    useInvalidateBranchAfterHistoryRewrite()
  const { mutateAsync: triggerSquash } = useSquashBranch()

  const reportFailure = useCallback(
    (reason: string, status: 'error' | 'compareWarning') => {
      setStatus(status)
      setError(reason)
    },
    [],
  )

  assertNotEmpty(conciseBranch)

  const mutateAsync = useCallback(
    async ({
      toBranch,
      upgradeBranchName,
      shouldSquash,
      shouldForce,
    }: UpgradeBranchInput & { shouldForce?: boolean }): Promise<
      Optional<UpgradeResult>
    > => {
      assertNotEmpty(toBranch)

      const {
        name: upgradeToBranchName,
        typesVersionIds: targetBranchTypesVersionIds,
      } = toBranch
      assertNotEmpty(upgradeToBranchName)

      setError('')
      setBranchChangesetsPackage(undefined)
      setStatus('comparing')

      try {
        const comparisonResult = await triggerUpgradeComparison({
          rhsBranchName: upgradeToBranchName,
          lhsBranchName: branchKey.name,
          componentName: branchKey.server.toLocaleLowerCase(),
        })
        if (!comparisonResult) {
          reportFailure(
            translate(
              'branchDetailsPanel.tabs.manage.upgrade.error.noComparison',
            ),
            'error',
          )
        } else if (
          // Changes exist on the left-hand-side branch that are not on the right-hand-side branch and partial upgrades exist
          comparisonResult.lhsOnly?.branchChangesets?.length &&
          comparisonResult.partialUpgrades?.branchChangesets?.length &&
          !shouldForce
        ) {
          reportFailure(
            translate(
              'branchDetailsPanel.tabs.manage.upgrade.error.compareWarning',
              {
                sourceBranchName: branchKey.name,
                targetBranchName: upgradeToBranchName,
              },
            ),
            'compareWarning',
          )
          setBranchChangesetsPackage(comparisonResult.lhsOnly)
        } else if (
          // No differences between branches
          !comparisonResult.completeUpgrades?.branchChangesets?.length &&
          !comparisonResult.partialUpgrades?.branchChangesets?.length
        ) {
          reportFailure(
            translate('branchDetailsPanel.tabs.manage.upgrade.error.noDiff', {
              sourceBranchName: branchKey.name,
              targetBranchName: upgradeToBranchName,
            }),
            'error',
          )
        } else {
          const upgradePackage = [
            ...(comparisonResult.completeUpgrades?.branchChangesets ?? []),
            ...(comparisonResult.partialUpgrades?.branchChangesets ?? []),
          ]

          assertNotEmpty(upgradePackage)

          const nextBranchChangesetsPackage = {
            ...upgradePackage,
            _type: 'BranchChangesets',
            branchChangesets: upgradePackage
              ?.filter(notEmpty)
              .map((branchChangeset) => ({
                ...branchChangeset,
                changeset: {
                  ...branchChangeset.changeset,
                  _type: 'ConfigurationChangeset',
                  description: translate(
                    'branchDetailsPanel.tabs.manage.upgrade.changesetDescription',
                    {
                      sourceBranchName: branchKey.name,
                      targetBranchName: upgradeToBranchName,
                    },
                  ),
                },
              })),
          } satisfies BranchChangesets
          setBranchChangesetsPackage(nextBranchChangesetsPackage)

          const changesetDescription = translate(
            'branchDetailsPanel.tabs.manage.upgrade.changesetDescription',
            {
              sourceBranchName: branchKey.name,
              targetBranchName: upgradeToBranchName,
            },
          )

          // The upgrade path depends on whether the targeted branch (the branch
          // the popover is opened on) is protected. Protected branches cannot
          // receive changes directly, so the upgrade lands on a newly created
          // child feature branch; non-protected branches receive the upgrade
          // changesets directly.
          const isProtectedTarget = isProtectedBranchCategory(
            conciseBranch.branchCategory,
          )
          const hostBranchName = isProtectedTarget
            ? upgradeBranchName
            : branchKey.name
          const hostBranchKey: BranchKey = {
            name: hostBranchName,
            server: branchKey.server,
          }

          if (isProtectedTarget) {
            setStatus('creatingBranch')

            await triggerCreateBranch({
              branchName: hostBranchName,
              baseBranch: conciseBranch,
              server: branchKey.server,
              category: 'feature',
              shouldDefineTypes: false,
              shouldAddData: false,
              typeVersionId: targetBranchTypesVersionIds?.at(0)?.typesVersionId,
            })
          }

          setStatus('addingChangesets')

          await triggerImportChangesets({
            server: branchKey.server,
            branchName: hostBranchName,
            configurationData: nextBranchChangesetsPackage,
          })

          // Protected targets always squash so the upgrade lands as a single
          // changeset on the child branch; for non-protected targets squashing
          // is opt-in via the form.
          if (isProtectedTarget || shouldSquash) {
            setStatus('squashingBranch')

            await triggerSquash({
              server: branchKey.server,
              branchName: hostBranchName,
              description: changesetDescription,
            })
          }

          invalidateBranches()
          invalidateBranchAfterHistoryRewrite(hostBranchKey)

          setStatus('success')

          return { hostBranchKey }
        }
      } catch {
        reportFailure(
          translate('branchDetailsPanel.tabs.manage.upgrade.error.unknown'),
          'error',
        )
      }

      return undefined
    },
    [
      branchKey.name,
      branchKey.server,
      conciseBranch,
      invalidateBranches,
      invalidateBranchAfterHistoryRewrite,
      reportFailure,
      translate,
      triggerCreateBranch,
      triggerImportChangesets,
      triggerSquash,
      triggerUpgradeComparison,
    ],
  )

  return useMemo(
    () => ({
      mutateAsync,
      status,
      error,
      configurationPackage: branchChangesetsPackage,
    }),
    [branchChangesetsPackage, error, status, mutateAsync],
  )
}
